/**
 * The "How it works" tab: ten chapters in the StepCard accordion, one open
 * at a time (driven by ?ch=), header dots + Start over, ←/→ to move, and
 * visited chapters remembered in the browser. Reads the plan, never writes.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, KeyboardEvent, useEffect } from 'react';

import type { DropGenPlanDetail } from '@trafficpulse/shared';

import { StepCardGroup } from '@/components/ui';

import { ChapterHeader } from './shell/ChapterHeader';
import type { BridgeParams } from './shell/TutorialBridge';
import { TUTORIAL_CHAPTERS } from './tutorial-chapters';
import { TutorialChapter } from './TutorialChapter';
import { useTutorialProgress } from './use-tutorial-progress';

const TOTAL = TUTORIAL_CHAPTERS.length;

export const TutorialTab: FC<{
  plan: DropGenPlanDetail;
  /** From ?ch=; null = the first chapter. */
  chapter: number | null;
  onChapter: (n: number | null) => void;
  onBridge: (params: BridgeParams) => void;
}> = ({ plan, chapter, onChapter, onBridge }) => {
  const open = chapter ?? 1;
  const { visited, markVisited, reset } = useTutorialProgress();
  useEffect(() => markVisited(open), [open, markVisited]);
  const go = (n: number) => onChapter(Math.min(TOTAL, Math.max(1, n)));
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') go(open + 1);
    if (e.key === 'ArrowLeft') go(open - 1);
  };
  return (
    <div className="flex flex-col gap-4 outline-none" tabIndex={-1} onKeyDown={onKeyDown}>
      <ChapterHeader
        open={open}
        total={TOTAL}
        visited={visited}
        onJump={go}
        onStartOver={() => {
          reset();
          go(1);
        }}
      />
      <StepCardGroup openIndex={open} onOpenChange={(i) => i !== null && go(i)}>
        {TUTORIAL_CHAPTERS.map((def) => (
          <TutorialChapter
            key={def.n}
            def={def}
            total={TOTAL}
            plan={plan}
            visited={visited.includes(def.n) && def.n !== open}
            onPrev={() => go(def.n - 1)}
            onNext={() => (def.n === TOTAL ? onBridge({ tab: null, ch: null }) : go(def.n + 1))}
            onBridge={onBridge}
          />
        ))}
      </StepCardGroup>
    </div>
  );
};
